import { useState, useCallback, Fragment, useEffect } from 'react'
import type { ReactNode, MouseEvent } from 'react'

// --- Loading spinner ---
export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const dims = { sm: 'h-4 w-4', md: 'h-6 w-6', lg: 'h-8 w-8' }
  return (
    <div
      className={`${dims[size]} animate-spin rounded-full border-2 border-border-default border-t-accent`}
    />
  )
}

// --- Error banner ---
export function ErrorBanner({ error }: { error: Error }) {
  const isIndexError = error.message.includes('no usable index')
  return (
    <div className="rounded-lg border border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-4 text-sm">
      <div className="flex items-start gap-2">
        <span className="text-red-600 dark:text-red-400 font-semibold">Error</span>
      </div>
      <p className="mt-1 text-red-700 dark:text-red-300 font-mono text-xs break-all">
        {error.message}
      </p>
      {isIndexError && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          The prodexer API enforces index usage. Add a filter on an indexed column
          (e.g. <code className="text-accent-text">block_height</code>,{' '}
          <code className="text-accent-text">sender_address</code>) to satisfy the
          index check.
        </p>
      )}
    </div>
  )
}

// --- Query execution wrapper hook ---
export function useQuery<T>() {
  const [data, setData] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const execute = useCallback(async (fn: () => Promise<T[]>) => {
    setLoading(true)
    setError(null)
    try {
      const result = await fn()
      setData(result)
      return result
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)))
      setData([])
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  return { data, loading, error, execute, setData }
}

// --- Section header ---
export function SectionHeader({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children?: ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <h2 className="text-lg font-semibold text-text-bright">{title}</h2>
        {subtitle && (
          <p className="text-sm text-text-muted mt-0.5">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  )
}

// --- Data table with client-side pagination + expandable rows ---
export function DataTable<T>({
  rows,
  columns,
  emptyMessage = 'No results',
  pageSize = 25,
  expandableKey,
  renderExpansion,
}: {
  rows: T[]
  columns: {
    key: keyof T | string
    label: string
    render?: (row: T) => ReactNode
    className?: string
  }[]
  emptyMessage?: string
  pageSize?: number
  expandableKey?: keyof T
  renderExpansion?: (row: T) => ReactNode
}) {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(pageSize)
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // Reset to page 0 when rows change (new query)
  const rowCount = rows.length
  useEffect(() => { setPage(0); setExpandedKey(null) }, [rowCount])

  if (rowCount === 0) {
    return (
      <div className="text-sm text-text-faint italic py-8 text-center">
        {emptyMessage}
      </div>
    )
  }

  const pageCount = Math.ceil(rowCount / size)
  const currentPage = Math.min(page, pageCount - 1)
  const start = currentPage * size
  const pageRows = rows.slice(start, start + size)
  const canExpand = !!expandableKey && !!renderExpansion

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-border-default">
        <table className="min-w-full divide-y divide-border-default">
          <thead className="bg-bg-secondary">
            <tr>
              {canExpand && <th className="w-8 px-2 py-2.5" />}
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  className={`px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-text-muted ${col.className ?? ''}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-faint">
            {pageRows.map((row, i) => {
              const rowKey = expandableKey ? String(row[expandableKey]) : String(i)
              const isExpanded = expandedKey === rowKey
              return (
                <Fragment key={rowKey}>
                  <tr
                    className={`transition-colors ${canExpand ? 'cursor-pointer' : ''} ${
                      isExpanded ? 'bg-bg-hover' : 'hover:bg-bg-hover'
                    }`}
                    onClick={canExpand ? () => setExpandedKey(isExpanded ? null : rowKey) : undefined}
                  >
                    {canExpand && (
                      <td className="px-2 py-2 text-text-muted text-xs select-none">
                        {isExpanded ? '▼' : '▶'}
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={String(col.key)}
                        className={`px-4 py-2 text-sm font-mono text-text-secondary ${col.className ?? ''}`}
                      >
                        {col.render
                          ? col.render(row)
                          : String(row[col.key as keyof T] ?? '—')}
                      </td>
                    ))}
                  </tr>
                  {canExpand && isExpanded && (
                    <tr>
                      <td colSpan={columns.length + 1} className="px-4 py-3 bg-bg-secondary">
                        {renderExpansion!(row)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      {pageCount > 1 && (
        <div className="mt-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-text-muted">
            <span>
              {start + 1}–{Math.min(start + size, rowCount)} of {rowCount}
            </span>
            <select
              value={size}
              onChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}
              className="rounded border border-input-border bg-input-bg px-1.5 py-0.5 text-xs text-text-primary focus:border-accent focus:outline-none"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-text-faint">/ page</span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(0)}
              disabled={currentPage === 0}
              aria-label="First page"
              className="rounded border border-border-default px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:border-border-strong disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ⟪
            </button>
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 0}
              aria-label="Previous page"
              className="rounded border border-border-default px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:border-border-strong disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ‹
            </button>
            <span className="px-2 text-xs font-mono text-text-secondary">
              {currentPage + 1} / {pageCount}
            </span>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage >= pageCount - 1}
              aria-label="Next page"
              className="rounded border border-border-default px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:border-border-strong disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ›
            </button>
            <button
              onClick={() => setPage(pageCount - 1)}
              disabled={currentPage >= pageCount - 1}
              aria-label="Last page"
              className="rounded border border-border-default px-2 py-1 text-xs text-text-muted hover:text-text-primary hover:border-border-strong disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              ⟫
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// --- JSON rendering helper (handles both string and parsed JSONB from pgx) ---
export function renderJsonData(data: unknown): string {
  if (typeof data === 'string') {
    try { return JSON.stringify(JSON.parse(data), null, 2) } catch { return data }
  }
  try { return JSON.stringify(data, null, 2) } catch { return String(data) }
}

// --- Click-to-copy hook ---
export function useClipboard(timeout = 1500) {
  const [copied, setCopied] = useState<string | null>(null)

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(text)
      setTimeout(() => setCopied(null), timeout)
    } catch {
      // Fallback for non-secure contexts (HTTP via Tailscale)
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try { document.execCommand('copy') } catch { /* ignore */ }
      document.body.removeChild(textarea)
      setCopied(text)
      setTimeout(() => setCopied(null), timeout)
    }
  }, [timeout])

  return { copied, copy }
}

// --- ClickToCopy component ---
// Wraps any text with click-to-copy behavior. Shows a checkmark tooltip.
export function ClickToCopy({
  text,
  display,
  className,
  title,
}: {
  text: string
  display?: ReactNode
  className?: string
  title?: string
}) {
  const { copied, copy } = useClipboard()
  const isCopied = copied === text

  return (
    <span
      onClick={(e) => { e.stopPropagation(); copy(text) }}
      className={`cursor-pointer inline-flex items-center gap-1 ${className ?? ''}`}
      title={title ?? `Click to copy: ${text}`}
    >
      {display ?? text}
      <span className={`text-xs transition-opacity ${isCopied ? 'opacity-100 text-success-text' : 'opacity-0'}`}>
        {isCopied ? '✓' : ''}
      </span>
    </span>
  )
}

// --- LinkToCopy component ---
// Shows a link that does client-side SPA navigation + a copy icon.
// Used for addresses (→ Wallet Explorer), tx hashes (→ Transaction Lookup),
// and block heights (→ Block Lookup).
// Ctrl/Cmd+click or middle-click opens in a new tab (browser default).
export function LinkToCopy({
  text,
  href,
  display,
  className,
  title,
}: {
  text: string
  href: string
  display?: ReactNode
  className?: string
  title?: string
}) {
  const { copied, copy } = useClipboard()
  const isCopied = copied === text

  const handleClick = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    // Let the browser handle ctrl/cmd+click and middle-click (new tab)
    if (e.ctrlKey || e.metaKey || e.button === 1) return
    // Client-side navigation: push state and dispatch popstate
    e.preventDefault()
    window.history.pushState(null, '', href)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [href])

  return (
    <span className={`inline-flex items-center gap-1 ${className ?? ''}`}>
      <a
        href={href}
        onClick={handleClick}
        className="hover:underline"
        title={title ?? text}
      >
        {display ?? truncate(text)}
      </a>
      <button
        onClick={(e) => { e.stopPropagation(); copy(text) }}
        className="text-text-faint hover:text-accent-text transition-colors"
        title={`Copy: ${text}`}
        aria-label={`Copy ${text}`}
      >
        {isCopied ? (
          <span className="text-success-text text-xs">✓</span>
        ) : (
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
    </span>
  )
}

// --- Format bytea tx_hash from base64 (API response) to hex for display ---
// Users expect Cosmos tx hashes as hex strings. Output is lowercase to match
// the standard Cosmos explorer format and PostgreSQL bytea hex convention.
export function txHashToDisplayHex(base64Hash: string): string {
  try {
    const binary = atob(base64Hash)
    let hex = ''
    for (let i = 0; i < binary.length; i++) {
      hex += binary.charCodeAt(i).toString(16).padStart(2, '0')
    }
    return hex
  } catch {
    // Already hex or unknown format — return as-is
    return base64Hash.toLowerCase()
  }
}

// --- Deep-link URL builders (for LinkToCopy hrefs) ---
// These build ?tool=X&q=Y URLs that App.tsx reads on page load.
export function walletUrl(address: string): string {
  return `?tool=wallet&q=${encodeURIComponent(address)}`
}

export function txUrl(txHash: string): string {
  return `?tool=tx&q=${encodeURIComponent(txHash)}`
}

export function blockUrl(height: string | number): string {
  return `?tool=block&q=${encodeURIComponent(String(height))}`
}

export function topicUrl(topicId: string | number): string {
  return `?tool=topic&q=${encodeURIComponent(String(topicId))}`
}

// --- Amount formatting ---
export function formatAmount(value: string | number, decimals = 6): string {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return String(value)
  // Allora amounts are in atto (1e18), convert to ALLO
  const allo = n / 1e18
  return allo.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals,
  })
}

// --- AmountDisplay: shows ALLO value with uALLO on hover ---
// Wraps formatAmount() output in a span with a title tooltip showing
// the raw uALLO (atto) value. Use anywhere an amount is displayed.
//
// Precision note: uALLO values exceed JavaScript's Number.MAX_SAFE_INTEGER
// (2^53). We use BigInt to preserve full precision for the tooltip.
// The API returns amounts as strings (sometimes in scientific notation
// like "1.866e17"), so we parse the string to a full integer string.
export function AmountDisplay({
  value,
  className,
  prefix,
  suffix,
}: {
  value: string | number
  className?: string
  prefix?: string
  suffix?: string
}) {
  const uallo = formatUAllo(value)
  return (
    <span
      title={`${uallo} uALLO`}
      className={className}
    >
      {prefix}{formatAmount(value)}{suffix}
    </span>
  )
}

/**
 * Format a uALLO value as a full-precision integer string for tooltips.
 * Handles scientific notation (e.g. "1.866e17"), plain integers, and floats.
 * Avoids parseFloat to preserve precision beyond Number.MAX_SAFE_INTEGER.
 */
function formatUAllo(value: string | number): string {
  const str = typeof value === 'number' ? value.toString() : value

  // Already a plain integer string — use BigInt directly
  if (/^\d+$/.test(str)) {
    try { return BigInt(str).toLocaleString() } catch { return str }
  }

  // Scientific notation (e.g. "1.8662208893985168e+17") — parse mantissa + exponent
  const sciMatch = str.match(/^(-?\d+(?:\.\d+)?)e([+-]?\d+)$/i)
  if (sciMatch) {
    const [, mantissaStr, expStr] = sciMatch
    const exp = parseInt(expStr, 10)
    const negative = mantissaStr.startsWith('-')
    const digits = mantissaStr.replace('-', '').replace('.', '')
    const decimalIdx = mantissaStr.indexOf('.')
    const fractionalLen = decimalIdx >= 0 ? mantissaStr.length - decimalIdx - 1 : 0
    const shift = exp - fractionalLen

    let result: string
    if (shift >= 0) {
      result = digits + '0'.repeat(shift)
    } else {
      // Would be a fractional number — round it
      const cut = digits.length + shift
      result = cut > 0 ? digits.slice(0, cut) : '0'
    }

    try {
      const big = BigInt((negative ? '-' : '') + result)
      return big.toLocaleString()
    } catch {
      return result
    }
  }

  // Decimal string (e.g. "1234.5678") — round to integer
  if (/^-?\d+\.\d+$/.test(str)) {
    try {
      // Split on decimal point and round manually
      const [intPart, fracPart] = str.split('.')
      const shouldRoundUp = parseInt(fracPart[0] || '0', 10) >= 5
      let big = BigInt(intPart)
      if (shouldRoundUp && !intPart.startsWith('-')) big++
      if (shouldRoundUp && intPart.startsWith('-')) big--
      return big.toLocaleString()
    } catch {
      return str
    }
  }

  // Fallback — return as-is
  return str
}

export function formatNumber(value: string | number): string {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return value as string
  return n.toLocaleString()
}

// --- Truncate address/hash for display ---
export function truncate(value: string | null | undefined, head = 10, tail = 6): string {
  if (!value) return '—'
  if (value.length <= head + tail + 3) return value
  return `${value.slice(0, head)}…${value.slice(-tail)}`
}

// --- Timestamp formatting ---
export function formatTime(ts: string | null): string {
  if (!ts) return '—'
  try {
    const d = new Date(ts)
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
    }) + ' UTC'
  } catch {
    return ts
  }
}

// --- Search input with submit + favorites dropdown + labeling ---
export function SearchInput({
  placeholder,
  onSubmit,
  loading,
  buttonText = 'Search',
  favorites = [],
  onToggleFavorite,
  onUpdateLabel,
  isFavorite = false,
  currentValue = '',
}: {
  placeholder: string
  onSubmit: (value: string) => void
  loading?: boolean
  buttonText?: string
  favorites?: { address: string; label: string }[]
  onToggleFavorite?: (address: string, label?: string) => void
  onUpdateLabel?: (address: string, label: string) => void
  isFavorite?: boolean
  currentValue?: string
}) {
  const [value, setValue] = useState('')
  const [showFavs, setShowFavs] = useState(false)
  const [labeling, setLabeling] = useState(false)
  const [labelInput, setLabelInput] = useState('')

  // Sync external value (e.g. from favorite click) into the input
  useEffect(() => { if (currentValue) setValue(currentValue) }, [currentValue])

  const submit = (val: string) => {
    if (val.trim()) onSubmit(val.trim())
    setShowFavs(false)
  }

  const handleStar = () => {
    const addr = value.trim()
    if (!addr) return
    if (isFavorite) {
      onToggleFavorite?.(addr)
    } else {
      // New favorite — show labeling input
      setLabeling(true)
      setLabelInput('')
    }
  }

  const confirmLabel = () => {
    onToggleFavorite?.(value.trim(), labelInput || undefined)
    setLabeling(false)
    setLabelInput('')
  }

  return (
    <div className="relative">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit(value)
        }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => setShowFavs(true)}
            onBlur={() => setTimeout(() => setShowFavs(false), 150)}
            placeholder={placeholder}
            className="w-full rounded-lg border border-input-border bg-input-bg px-4 py-2 pr-9 text-sm font-mono text-text-primary placeholder-text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent transition-colors"
          />
          {/* Star button */}
          {onToggleFavorite && value.trim() && (
            <button
              type="button"
              onClick={handleStar}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-text-faint hover:text-amber-text transition-colors"
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            >
              {isFavorite ? '★' : '☆'}
            </button>
          )}
        </div>

        {favorites.length > 0 && (
          <button
            type="button"
            onClick={() => setShowFavs(!showFavs)}
            className="rounded-lg border border-border-default bg-surface px-3 py-2 text-sm text-text-muted hover:text-text-primary hover:border-border-strong transition-colors"
            title="Favorite addresses"
            aria-label="Show favorite addresses"
          >
            ★
          </button>
        )}

        <button
          type="submit"
          disabled={loading || !value.trim()}
          className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <Spinner size="sm" /> : buttonText}
        </button>
      </form>

      {/* Inline labeling input (shown when adding a new favorite) */}
      {labeling && (
        <div className="mt-2 flex items-center gap-2 rounded-lg border border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/20 p-2">
          <span className="text-xs text-text-muted whitespace-nowrap">Label:</span>
          <input
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); confirmLabel() }
              if (e.key === 'Escape') { setLabeling(false); setLabelInput('') }
            }}
            placeholder="e.g. Treasury, Hot Wallet, Exchange..."
            autoFocus
            className="flex-1 rounded border border-input-border bg-input-bg px-2 py-1 text-sm text-text-primary placeholder-text-faint focus:border-accent focus:outline-none"
          />
          <button
            onClick={confirmLabel}
            className="rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent-hover transition-colors"
          >
            Save
          </button>
          <button
            onClick={() => { setLabeling(false); setLabelInput('') }}
            className="rounded border border-border-default px-3 py-1 text-xs text-text-muted hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Favorites dropdown */}
      {showFavs && favorites.length > 0 && !labeling && (
        <div className="absolute z-20 mt-1 w-full max-w-md rounded-lg border border-border-default bg-surface shadow-lg">
          <div className="px-3 py-2 text-xs uppercase tracking-wider text-text-faint border-b border-border-faint">
            Favorite Addresses
          </div>
          <div className="max-h-60 overflow-y-auto">
            {favorites.map((fav) => (
              <div
                key={fav.address}
                className="flex items-center hover:bg-bg-hover transition-colors"
              >
                <button
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setValue(fav.address)
                    submit(fav.address)
                  }}
                  className="flex flex-1 items-center gap-2 px-3 py-2 text-left min-w-0"
                >
                  <span className="text-amber-text text-xs">★</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-mono text-text-primary truncate">
                      {fav.address}
                    </div>
                    {fav.label !== fav.address && fav.label && (
                      <div className="text-xs text-text-muted truncate">{fav.label}</div>
                    )}
                  </div>
                </button>
                {onUpdateLabel && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault()
                      const newLabel = prompt('Edit label:', fav.label)
                      if (newLabel !== null) onUpdateLabel(fav.address, newLabel)
                    }}
                    className="px-2 py-2 text-text-faint hover:text-text-muted transition-colors"
                    title="Edit label"
                    aria-label="Edit label"
                  >
                    ✎
                  </button>
                )}
                {onToggleFavorite && (
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault()
                      onToggleFavorite(fav.address)
                    }}
                    className="px-2 py-2 text-text-faint hover:text-danger-text transition-colors"
                    title="Remove from favorites"
                    aria-label="Remove from favorites"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Stat card ---
export function StatCard({
  label,
  value,
  sublabel,
  accent = 'default',
}: {
  label: string
  value: string | number
  sublabel?: string
  accent?: 'default' | 'green' | 'red' | 'blue' | 'amber'
}) {
  const borderAccents = {
    default: 'border-border-default',
    green: 'border-emerald-300 dark:border-emerald-900/50',
    red: 'border-red-300 dark:border-red-900/50',
    blue: 'border-sky-300 dark:border-sky-900/50',
    amber: 'border-amber-300 dark:border-amber-900/50',
  }
  const valueColors = {
    default: 'text-text-bright',
    green: 'text-success-text',
    red: 'text-danger-text',
    blue: 'text-accent-text',
    amber: 'text-amber-text',
  }
  return (
    <div className={`rounded-lg border ${borderAccents[accent]} bg-surface p-4`}>
      <div className="text-xs uppercase tracking-wider text-text-muted">
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold font-mono ${valueColors[accent]}`}>
        {value}
      </div>
      {sublabel && (
        <div className="text-xs text-text-faint mt-0.5">{sublabel}</div>
      )}
    </div>
  )
}
